import React, { createContext, useState, useCallback, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import filesize from 'filesize';

import api from '~/services/api';

export interface IPost {
  file_temp_id: string;
  name: string;
  size: number;
  key: string;
  url: string;
}

export interface IStoreRelationshipTopicFile {
  id: string;
  name: string;
  readableSize: string;
  type: string;
  uploaded?: boolean;
  preview: string;
  file: File | null;
  progress?: number;
  error?: boolean;
  url: string;
}

interface IStoreRelationshipTopicFileContextData {
  uploadedStoreRelationshipTopicFiles: IStoreRelationshipTopicFile[];
  deleteStoreRelationshipTopicFile(id: string): void;
  handleUpload(
    file: any,
    type: string,
    storeRelationshipTopicTempId: string,
    userId: string,
  ): void;
}

const StoreRelationshipTopicFileContext =
  createContext<IStoreRelationshipTopicFileContextData>(
    {} as IStoreRelationshipTopicFileContextData,
  );

const StoreRelationshipTopicFileProvider: React.FC = ({ children }) => {
  const [
    uploadedStoreRelationshipTopicFiles,
    setUploadedStoreRelationshipTopicFiles,
  ] = useState<IStoreRelationshipTopicFile[]>([]);

  const updateStoreRelationshipTopicFile = useCallback((id, data) => {
    setUploadedStoreRelationshipTopicFiles(state =>
      state.map(file => (file.id === id ? { ...file, ...data } : file)),
    );
  }, []);

  const processUpload = useCallback(
    (
      uploadedStoreRelationshipTopicFile: IStoreRelationshipTopicFile,
      type: string,
      storeRelationshipTopicTempId: string,
      userId: string,
    ) => {
      const data = new FormData();
      if (uploadedStoreRelationshipTopicFile.file) {
        data.append('type', type);
        data.append(
          'store_relationship_topic_temp_id',
          storeRelationshipTopicTempId,
        );
        data.append('file_temp_id', uploadedStoreRelationshipTopicFile.id);
        data.append('user_id', userId);
        data.append(
          'file',
          uploadedStoreRelationshipTopicFile.file,
          uploadedStoreRelationshipTopicFile.name,
        );
      }

      api
        .post('/storerelationshiptopicfilesreceived', data, {
          onUploadProgress: progressEvent => {
            const progress: number = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            );

            updateStoreRelationshipTopicFile(
              uploadedStoreRelationshipTopicFile.id,
              { progress },
            );
          },
        })
        .then(response => {
          updateStoreRelationshipTopicFile(
            uploadedStoreRelationshipTopicFile.id,
            {
              uploaded: true,
              id: response.data.id,
              url: response.data.url,
            },
          );
        })
        .catch(err => {
          console.log(err);
          updateStoreRelationshipTopicFile(
            uploadedStoreRelationshipTopicFile.id,
            {
              error: true,
            },
          );
        });
    },
    [updateStoreRelationshipTopicFile],
  );

  const handleUpload = useCallback(
    (
      files: File[],
      type: string,
      storeRelationshipTopicTempId: string,
      userId: string,
    ) => {
      const newUploadedStoreRelationshipTopicFiles: IStoreRelationshipTopicFile[] =
        files.map((file: File) => ({
          file,
          id: uuidv4(),
          name: file.name,
          readableSize: filesize(file.size),
          type: file.type,
          preview: URL.createObjectURL(file),
          progress: 0,
          uploaded: false,
          error: false,
          url: '',
        }));

      setUploadedStoreRelationshipTopicFiles(state =>
        state.concat(newUploadedStoreRelationshipTopicFiles),
      );
      newUploadedStoreRelationshipTopicFiles.forEach(
        upStoreRelationshipTopicFile =>
          processUpload(
            upStoreRelationshipTopicFile,
            type,
            storeRelationshipTopicTempId,
            userId,
          ),
      );
    },
    [processUpload],
  );

  const deleteStoreRelationshipTopicFile = useCallback((id: string) => {
    api.delete(`storerelationshiptopicfilesreceived/${id}`);
    setUploadedStoreRelationshipTopicFiles(state =>
      state.filter(file => file.id !== id),
    );
  }, []);

  return (
    <StoreRelationshipTopicFileContext.Provider
      value={{
        uploadedStoreRelationshipTopicFiles,
        deleteStoreRelationshipTopicFile,
        handleUpload,
      }}
    >
      {children}
    </StoreRelationshipTopicFileContext.Provider>
  );
};

function useStoreRelationshipTopicFiles(): IStoreRelationshipTopicFileContextData {
  const context = useContext(StoreRelationshipTopicFileContext);

  if (!context) {
    throw new Error(
      'useStoreRelationshipTopicFiles deve ser usado ' +
        'dentro de um StoreRelationshipTopicFileProvider',
    );
  }

  return context;
}

export { StoreRelationshipTopicFileProvider, useStoreRelationshipTopicFiles };
